import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { useIntakeCounts } from "../../app/IntakeCountsContext";
import { FileDropzone } from "../../components/FileDropzone";
import { useConfirmDialog } from "../../components/ConfirmDialog";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.gif,.docx,.doc";

type NominationStatus = "Extracted" | "InReview" | "Committed" | "Rejected" | "Superseded";
type ParseStatus = "Pending" | "Parsing" | "Parsed" | "Unreadable" | "Failed" | "Quarantined";

interface QueueItem {
  nominationId: number;
  fileName: string;
  producerNameRaw: string | null;
  status: NominationStatus;
  parseStatus: ParseStatus;
  pageCount: number | null;
  fieldsNeedingReview: number;
  receivedUtc: string;
}

interface Evidence {
  page: number;
  text: string;
  charStart: number | null;
  charEnd: number | null;
}

interface DocTextPage {
  page: number;
  text: string;
}

interface Field {
  fieldKey: string;
  label: string;
  extractedValue: string | null;
  finalValue: string | null;
  confidence: number | null;
  flagReason: string | null;
  required: boolean;
  wasEdited: boolean;
  wasConfirmed: boolean;
  evidence: Evidence[];
}

interface NominationDetail {
  nominationId: number;
  fileName: string;
  pageCount: number | null;
  status: NominationStatus;
  parseStatus: ParseStatus;
  extractorName: string | null;
  extractorVersion: string | null;
  confidenceThreshold: number;
  canCommit: boolean;
  createdByAppUserId: number;
  fields: Field[];
}

const isImage = (name: string) => /\.(jpe?g|png|gif)$/i.test(name);
const isPdf = (name: string) => /\.pdf$/i.test(name);
const isOffice = (name: string) => /\.docx?$/i.test(name);

function needsWork(detail: NominationDetail): Field[] {
  return detail.fields.filter(
    (f) => f.confidence !== null && f.confidence < detail.confidenceThreshold && !f.wasConfirmed,
  );
}

function renderHighlightedPage(
  text: string,
  page: number,
  fields: Field[],
  confidenceThreshold: number,
  hovered: string | null,
): ReactNode[] {
  const spans = fields
    .map((f) => ({ field: f, evidence: f.evidence[0] }))
    .filter(
      (x) =>
        x.evidence && x.evidence.page === page && x.evidence.charStart != null && x.evidence.charEnd != null,
    )
    .map((x) => ({
      fieldKey: x.field.fieldKey,
      start: x.evidence!.charStart as number,
      end: x.evidence!.charEnd as number,
      low: x.field.confidence !== null && x.field.confidence < confidenceThreshold,
    }))
    .sort((a, b) => a.start - b.start);

  const nodes: ReactNode[] = [];
  let cursor = 0;
  for (const span of spans) {
    if (span.start < cursor) continue;
    if (span.start > cursor) nodes.push(text.slice(cursor, span.start));
    nodes.push(
      <mark key={`${span.fieldKey}-${span.start}`} className={`${span.low ? "low" : ""} ${hovered === span.fieldKey ? "hit" : ""}`.trim()}>
        {text.slice(span.start, span.end)}
      </mark>,
    );
    cursor = span.end;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

export function NomLane() {
  const apiFetch = useApiFetch();
  const { appRole, appUserId } = useAppUser();
  const canManage = (createdByAppUserId: number | null) =>
    appRole === "Admin" || (appRole === "Uploader" && createdByAppUserId === appUserId);
  const { setCounts } = useIntakeCounts();
  const { confirm, dialog } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();

  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueFilter, setQueueFilter] = useState<"Open" | "All">("Open");
  const [queueLoading, setQueueLoading] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [active, setActive] = useState<NominationDetail | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentText, setDocumentText] = useState<DocTextPage[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const addFilesInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const loadQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await apiFetch(`nominations/queue?status=${queueFilter}`);
      setQueue(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFilter]);

  useEffect(() => {
    setCounts({
      lettersInQueue: queue.filter((q) => q.status === "Extracted" || q.status === "InReview").length,
      fieldsToReview: queue.reduce((a, q) => a + q.fieldsNeedingReview, 0),
      lettersCommitted: queue.filter((q) => q.status === "Committed").length,
    });
  }, [queue, setCounts]);

  const loadDetail = async (nominationId: number) => {
    const res = await apiFetch(`nominations/${nominationId}`);
    const detail: NominationDetail = await res.json();
    setActive(detail);
    return detail;
  };

  const loadDocument = async (nominationId: number) => {
    try {
      const res = await apiFetch(`nominations/${nominationId}/document`);
      const doc: { url: string } = await res.json();
      setDocumentUrl(doc.url);
    } catch {
      setDocumentUrl(null);
    }
  };

  const selectNomination = async (nominationId: number) => {
    setActiveId(nominationId);
    setActive(null);
    setDocumentUrl(null);
    setDocumentText(null);
    try {
      await loadDetail(nominationId);
      await loadDocument(nominationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    const openId = Number(searchParams.get("open"));
    if (openId) {
      selectNomination(openId);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetched once a letter's parseStatus flips to Parsed, whether that's
  // from selecting an already-parsed letter or from a poll tick catching
  // the transition. Null for letters parsed before this endpoint existed.
  useEffect(() => {
    if (!activeId || active?.parseStatus !== "Parsed") {
      setDocumentText(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`nominations/${activeId}/text`);
        const body: { pages: DocTextPage[] | null } = await res.json();
        if (!cancelled) setDocumentText(body.pages);
      } catch {
        if (!cancelled) setDocumentText(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active?.parseStatus]);

  // Only the active detail is refetched on each tick -- refetching the whole
  // queue every 2s (previously done here too) doubled the request volume for
  // no benefit while waiting, and re-rendered the entire left-hand list on
  // every tick, which read as constant flashing. The queue is refreshed once
  // after polling settles, in uploadFiles below.
  const pollUntilParsed = async (nominationId: number) => {
    for (let i = 0; i < 40; i++) {
      const detail = await loadDetail(nominationId);
      if (detail.parseStatus === "Parsed" || detail.parseStatus === "Failed") return;
      await new Promise((r) => setTimeout(r, 2000));
    }
    if (activeId === nominationId) {
      setError("Reading this document is taking longer than expected. Reload to check again.");
    }
  };

  const uploadFiles = async (files: FileList) => {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      [...files].forEach((f) => form.append("files", f));
      const res = await apiFetch("nominations/files", { method: "POST", body: form });
      const created: QueueItem[] = await res.json();
      await loadQueue();
      if (created.length) {
        await selectNomination(created[0].nominationId);
        await Promise.all(created.map((c) => pollUntilParsed(c.nominationId)));
        await loadQueue();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const updateField = async (fieldKey: string, finalValue: string, confirmed: boolean) => {
    if (!active) return;
    // Optimistic local update so typing/checking feels instant -- but
    // canCommit is a server-computed aggregate across every field's
    // confidence/confirmation state, not something this per-field patch can
    // derive locally. Without applying the PATCH response below, canCommit
    // stayed stuck at whatever it was on the last full load, so the Commit
    // button could still read disabled even once every field was confirmed.
    setActive({
      ...active,
      fields: active.fields.map((f) => (f.fieldKey === fieldKey ? { ...f, finalValue, wasConfirmed: confirmed, wasEdited: true } : f)),
    });
    try {
      const res = await apiFetch(`nominations/${active.nominationId}/fields`, {
        method: "PATCH",
        body: JSON.stringify({ fields: [{ fieldKey, finalValue, confirmed }] }),
      });
      setActive(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await loadDetail(active.nominationId);
    }
  };

  const commit = async () => {
    if (!active) return;
    setCommitting(true);
    setError(null);
    try {
      await apiFetch(`nominations/${active.nominationId}/commit`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await loadDetail(active.nominationId);
      await loadQueue();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError((err.body as { detail?: string })?.detail ?? "Cannot commit: some fields still need confirming.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setCommitting(false);
    }
  };

  const rejectOne = async (nominationId: number) => {
    const reason = await confirm({
      title: "Remove letter",
      message: "Remove this letter from the queue?",
      reasonLabel: "Reason (shown in the audit trail)",
    });
    if (reason === null) return;
    try {
      await apiFetch(`nominations/${nominationId}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: reason || "Removed from queue" }),
      });
      if (activeId === nominationId) {
        setActiveId(null);
        setActive(null);
        setDocumentUrl(null);
      }
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteRejected = async (nominationId: number) => {
    const result = await confirm({
      title: "Delete letter",
      message: "Permanently delete this rejected letter? This cannot be undone.",
    });
    if (result === null) return;
    try {
      await apiFetch(`nominations/${nominationId}`, { method: "DELETE" });
      if (activeId === nominationId) {
        setActiveId(null);
        setActive(null);
        setDocumentUrl(null);
      }
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const clearQueue = async () => {
    const open = queue.filter((q) => q.status === "Extracted" || q.status === "InReview");
    if (!open.length) return;
    const result = await confirm({
      title: "Clear queue",
      message: `Remove all ${open.length} open letter(s) from the queue? This cannot be undone from here.`,
    });
    if (result === null) return;
    setError(null);
    try {
      await Promise.all(
        open.map((q) =>
          apiFetch(`nominations/${q.nominationId}/reject`, {
            method: "POST",
            body: JSON.stringify({ reason: "Cleared via bulk queue action" }),
          }),
        ),
      );
      setActiveId(null);
      setActive(null);
      setDocumentUrl(null);
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const openCount = queue.filter((q) => q.status === "Extracted" || q.status === "InReview").length;

  return (
    <>
    <section className="lane on">
      <h2>Producer Price Nominations</h2>
      <p className="lede">
        Upload a price letter — PDF, a photo or scan (JPG/PNG/GIF), or a Word document
        (.docx/.doc) — and it's read automatically. Anything the extractor is unsure of is
        held for review: confirm each flagged field against the source document before the
        nomination is written to the price table.
      </p>

      {queue.length === 0 && (
        <div className="sec">
          {appRole === "Viewer" ? (
            <div className="gate narrow">
              <span className="msg">Your account has view-only access. Ask an Admin for Uploader access to upload letters.</span>
            </div>
          ) : (
            <FileDropzone
              accept={ACCEPT}
              multiple
              title="Drop a price letter here"
              hint="PDF, JPG, PNG, GIF, DOCX, or DOC"
              onFiles={uploadFiles}
            />
          )}
        </div>
      )}

      {error && (
        <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
      )}

      <div className="sec">
        <div className="work">
          <div className="pane queue">
            <div className="pane-hd">
              <span>Queue</span>
              <select
                className="ctl"
                style={{ padding: "2px 6px", fontSize: 12 }}
                value={queueFilter}
                onChange={(e) => setQueueFilter(e.target.value as "Open" | "All")}
              >
                <option value="Open">Open</option>
                <option value="All">All</option>
              </select>
            </div>
            {queue.length > 0 && appRole !== "Viewer" && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 6, padding: "6px 10px", borderBottom: "1px solid var(--line)" }}>
                <button
                  className="btn"
                  style={{ padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap", gap: 4 }}
                  disabled={uploading}
                  onClick={() => addFilesInputRef.current?.click()}
                  title="Add more letters to the queue"
                >
                  <span className={`material-symbols-sharp${uploading ? " spin" : ""}`} style={{ fontSize: 15 }}>
                    {uploading ? "progress_activity" : "add"}
                  </span>
                  {uploading ? "Uploading…" : "Add Files"}
                </button>
                <input
                  ref={addFilesInputRef}
                  type="file"
                  accept={ACCEPT}
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files?.length) uploadFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                {openCount > 0 && (
                  <button
                    className="btn ghost"
                    style={{ padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap", gap: 4 }}
                    onClick={clearQueue}
                    title="Remove every open letter from the queue"
                  >
                    <span className="material-symbols-sharp" style={{ fontSize: 15 }}>delete_sweep</span>Clear Queue
                  </button>
                )}
              </div>
            )}
            <div className="pane-bd">
              {queueLoading && <div className="empty">Loading…</div>}
              {!queueLoading && queue.length === 0 && (
                <div className="empty"><span className="material-symbols-sharp">inbox</span>Nothing in the queue.</div>
              )}
              {queue.map((q) => (
                <button
                  key={q.nominationId}
                  className={`qitem${q.status === "Committed" || q.status === "Rejected" ? " qdone" : ""}`}
                  aria-current={q.nominationId === activeId}
                  onClick={() => selectNomination(q.nominationId)}
                >
                  <div className="p">{q.producerNameRaw ?? "Reading…"}</div>
                  <div className="f">{q.fileName}</div>
                  <div className="s">
                    {q.status === "Committed" ? (
                      <span className="chip ok"><span className="material-symbols-sharp">check</span>Committed</span>
                    ) : q.status === "Rejected" ? (
                      <span className="chip bad"><span className="material-symbols-sharp">block</span>Removed</span>
                    ) : q.parseStatus === "Pending" || q.parseStatus === "Parsing" ? (
                      <span className="chip info"><span className="material-symbols-sharp spin">progress_activity</span>Reading…</span>
                    ) : q.parseStatus === "Failed" ? (
                      <span className="chip bad"><span className="material-symbols-sharp">error</span>Could not read file</span>
                    ) : q.fieldsNeedingReview > 0 ? (
                      <span className="chip bad"><span className="material-symbols-sharp">rate_review</span>{q.fieldsNeedingReview} to review</span>
                    ) : (
                      <span className="chip ok"><span className="material-symbols-sharp">done_all</span>Ready to commit</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="pane">
            <div className="pane-hd">
              <span>{active ? active.fileName : "No letter selected"}</span>
              <span style={{ display: "flex", gap: 8 }}>
                {active && active.pageCount && <span className="chip">{active.pageCount} {active.pageCount > 1 ? "pages" : "page"}</span>}
                {active && active.status === "Rejected" && (
                  <button
                    className="btn ghost"
                    disabled={!canManage(active.createdByAppUserId)}
                    title={!canManage(active.createdByAppUserId) ? "You can only delete letters you uploaded, unless you're an Admin" : "Permanently delete this rejected letter"}
                    onClick={() => deleteRejected(active.nominationId)}
                  >
                    <span className="material-symbols-sharp">delete_forever</span>Delete
                  </button>
                )}
                {active && active.status !== "Rejected" && (
                  <button
                    className="btn ghost"
                    disabled={!canManage(active.createdByAppUserId)}
                    title={!canManage(active.createdByAppUserId) ? "You can only remove letters you uploaded, unless you're an Admin" : undefined}
                    onClick={() => rejectOne(active.nominationId)}
                  >
                    <span className="material-symbols-sharp">delete</span>Remove
                  </button>
                )}
              </span>
            </div>
            <div className="pane-bd">
              {!active && (
                <div className="empty"><span className="material-symbols-sharp">draft</span>Pick a letter from the queue.</div>
              )}
              {active && active.parseStatus !== "Parsed" && (
                <div className="empty">
                  <span className={`material-symbols-sharp${active.parseStatus === "Failed" ? "" : " spin"}`}>
                    {active.parseStatus === "Failed" ? "error" : "progress_activity"}
                  </span>
                  {active.parseStatus === "Failed" ? "This file could not be read." : "Reading the document…"}
                </div>
              )}
              {active && active.parseStatus === "Parsed" && documentText && documentText.length > 0 && (
                <div className="paperwrap">
                  <div className="paper">
                    {documentText.map((p) => (
                      <div key={p.page} style={{ whiteSpace: "pre-wrap" }}>
                        {renderHighlightedPage(p.text, p.page, active.fields, active.confidenceThreshold, hovered)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {active && active.parseStatus === "Parsed" && documentText && documentText.length > 0 && documentUrl && (
                <div style={{ padding: "10px 16px", fontSize: 12, color: "var(--grey)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
                  Original document
                </div>
              )}
              {active && active.parseStatus === "Parsed" && documentUrl && (
                <>
                  {(isPdf(active.fileName) || isImage(active.fileName)) && (
                    <div style={{ padding: 22, display: "flex", justifyContent: "center", background: "#101208" }}>
                      {isPdf(active.fileName) && (
                        <iframe
                          title="Source document"
                          src={documentUrl}
                          style={{ width: "100%", maxWidth: 660, height: "auto", aspectRatio: "8.5 / 11", border: 0 }}
                        />
                      )}
                      {isImage(active.fileName) && (
                        <img src={documentUrl} alt="Source document" style={{ maxWidth: "100%" }} />
                      )}
                    </div>
                  )}
                  {isOffice(active.fileName) && (
                    <div className="empty">
                      <span className="material-symbols-sharp">description</span>
                      Word documents can't be previewed inline yet.
                      <a href={documentUrl} target="_blank" rel="noopener noreferrer">Open the Original File</a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="pane fields">
            <div className="pane-hd">
              <span>Extracted fields</span>
            </div>
            <div className="pane-bd">
              {!active && (
                <div className="empty"><span className="material-symbols-sharp">checklist</span>Field-by-field review appears here.</div>
              )}
              {active && active.parseStatus === "Parsed" && active.fields.map((f) => {
                const low = f.confidence !== null && f.confidence < active.confidenceThreshold;
                const pct = f.confidence !== null ? Math.round(f.confidence * 100) : null;
                const evidence = f.evidence[0];
                return (
                  <div
                    key={f.fieldKey}
                    className={`frow ${low && !f.wasConfirmed ? "needs" : low && f.wasConfirmed ? "done" : ""}`}
                    onMouseEnter={() => setHovered(f.fieldKey)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div className="frow-t">
                      <span className="lbl">{f.label}{f.required && <span className="req">*</span>}</span>
                      {pct !== null && (
                        <span className="conf">
                          <span className={`bar ${low ? "low" : ""}`}><i style={{ width: `${pct}%` }} /></span>{pct}%
                        </span>
                      )}
                    </div>
                    <input
                      className="fval"
                      value={f.finalValue ?? ""}
                      onChange={(e) => updateField(f.fieldKey, e.target.value, f.wasConfirmed)}
                      aria-label={f.label}
                      disabled={!canManage(active.createdByAppUserId)}
                    />
                    {low && f.flagReason && (
                      <div className="why"><span className="material-symbols-sharp">error</span><span>{f.flagReason}</span></div>
                    )}
                    {evidence && hovered === f.fieldKey && (
                      <div className="why">
                        <span className="material-symbols-sharp">format_quote</span>
                        <span>"{evidence.text}" (page {evidence.page})</span>
                      </div>
                    )}
                    {low && (
                      <label className="confirm">
                        <input
                          type="checkbox"
                          checked={f.wasConfirmed}
                          onChange={(e) => updateField(f.fieldKey, f.finalValue ?? "", e.target.checked)}
                        />
                        <span>{f.wasConfirmed ? "Confirmed against the source" : "Confirm against the source"}</span>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            {active && active.parseStatus === "Parsed" && (
              <div className="commitbar">
                <div className="st">
                  {active.status === "Committed" ? (
                    <>
                      <span className="material-symbols-sharp" style={{ fontSize: 15, color: "var(--lime)" }}>check_circle</span>
                      <span>Written to the nomination table with the source file and confidence scores attached.</span>
                    </>
                  ) : active.status === "Rejected" ? (
                    <>
                      <span className="material-symbols-sharp" style={{ fontSize: 15, color: "var(--salmon)" }}>block</span>
                      <span>Removed from the queue.</span>
                    </>
                  ) : needsWork(active).length > 0 ? (
                    <>
                      <span className="material-symbols-sharp" style={{ fontSize: 15, color: "var(--salmon)" }}>error</span>
                      <span>{needsWork(active).length} field{needsWork(active).length > 1 ? "s" : ""} still need{needsWork(active).length > 1 ? "" : "s"} confirming. Hover a field to see its source quote.</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-sharp" style={{ fontSize: 15, color: "var(--lime)" }}>check_circle</span>
                      <span>Every flagged field is confirmed. Committing writes this nomination to the price table.</span>
                    </>
                  )}
                </div>
                <button
                  className="btn"
                  disabled={
                    (active.status !== "Extracted" && active.status !== "InReview") ||
                    !active.canCommit ||
                    committing
                  }
                  onClick={commit}
                >
                  <span className="material-symbols-sharp">{active.status === "Committed" ? "check" : "library_add"}</span>
                  {active.status === "Committed" ? "Committed" : committing ? "Committing…" : "Commit Nomination"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    </section>
    {dialog}
    </>
  );
}
